import {
  booleanAttribute,
  Component,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  Output,
  ViewEncapsulation
} from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, NgControl, ValidationErrors } from "@angular/forms";
import { SharedModule } from "../../../modules/shared/shared.module";
import { SvgIconComponent } from "../../svg-icon/svg-icon.component";
import {
  DateTimeAdapter,
  OwlDateTimeModule,
  OwlNativeDateTimeModule,
  PickerType
} from "@danielmoncada/angular-datetime-picker";
import { LOCALES } from "../../../constants/app-constants";
import { NgClass } from "@angular/common";

type DateTimeType = "date" | "datetime-local" | "time" | "month";

@Component({
  selector: "app-date-picker",
  standalone: true,
  imports: [SvgIconComponent, OwlDateTimeModule, OwlNativeDateTimeModule, NgClass, FormsModule],
  templateUrl: "./date-picker.component.html",
  styleUrl: "./date-picker.component.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None
})
export class DatePickerComponent implements ControlValueAccessor {
  @Input() type: 'date'| 'datetime-local' | string = "date";
  @Input() placeholder: string = "Select a date";
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() minDate: string = "";
  @Input() maxDate: string = "";
  @Input() defaultToCurrentDate: boolean = false;

  ngControl: NgControl | null = null;
  defaultPlaceHolder: string = "Enter Date";
  DateTimeTypesVsPickerType: Record<string, PickerType> = {
    "date": "calendar",
    "datetime-local": "both",
    "time": "timer",
    "month": "calendar"
  };

  @Output() dateChange = new EventEmitter<string>();

  value: string = "";
  // displayValue: string = '';

  onChange: any = () => {
  };
  onTouched: any = () => {
  };

  constructor(private dateTimeAdapter: DateTimeAdapter<any>,private injector: Injector) {
    this.dateTimeAdapter.setLocale(navigator.language || LOCALES.EN_IN.value);
  }

  ngOnInit(){
    this.ngControl = this.injector.get(NgControl, null);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  writeValue(value: string): void {
    if (this.defaultToCurrentDate && !value) {
      const currentDate = this.getDateString(new Date());
      this.value = currentDate;
      // this.displayValue = this.formatDisplayValue(currentDate);
      setTimeout(() => {
        this.onChange(currentDate);
        this.onTouched();
        this.dateChange.emit(currentDate);
      });
    } else {
      const parsedDate = this.parseDateValue(value);
      if (!parsedDate) {
        this.value = "";
        return;
      }
      this.value = this.getDateString(parsedDate);
      // this.displayValue = this.formatDisplayValue(value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onDateChange(date: any): void {
    this.value = date.value ? this.getDateString(date.value) : ""
    // this.displayValue = this.formatDisplayValue(this.value);
    this.onChange(this.value);
    this.onTouched();
    this.dateChange.emit(this.value);
  }

  //TODO: Remove post discussion
  private formatDisplayValue(value: string): string {
    if (!value) return "";

    const date = new Date(value);
    const dateOptions: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "numeric",
      year: "numeric"
    };

    if (this.type === "date") {
      return date.toLocaleDateString([], dateOptions);
    } else {
      const datePart = date.toLocaleDateString([], dateOptions);
      const timePart = date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true
      }).toUpperCase(); // Convert entire time string to uppercase

      return `${datePart}, ${timePart}`;
    }
  }

  private getDateString(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");

    switch (this.type) {
      case "date":
        return `${year}-${month}-${day}`;
      case "datetime-local":
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      case "time":
        return `${hours}:${minutes}`;
      case "month":
        return `${year}-${month}`;
      default:
        return "";
    }
  }

  private parseDateValue(value: string | number): Date | null {
    if (!value) return null;
    if (typeof value === "number") {
      return new Date(value);
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  hasError() {
    return this.ngControl?.control?.invalid && this.ngControl?.control?.touched && this.ngControl?.control?.errors;
  }
}
